import { Baseline, BaselineFrame, CommonData, KeyStage, PlacementData, ProgressReportInfo, StudentGroupSession, StudentSession, TargetGrade } from '../../_models/tutor-reports/tutorreport';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SessionSelectedDateInfo } from '../../_models/session';
import { PlacementMaintenance } from '../../_models/maintenance/placement-maintenance.model';
import { PlacementService } from '../../_services/placementservice';

@Injectable({
  providedIn: 'root'
})
export class TutorReportsHelperService {

  public commonData: CommonData;
  public baseline: Baseline;
  public keyStages: KeyStage[];
  public targetGrades: TargetGrade[];
  public assessmentRequired: boolean = true;
  public baselineTargetForOutcome: number;
  public defaultIndividualLearningPlan: string;

  public progressReport: ProgressReportInfo = new ProgressReportInfo();
  public studentSession: StudentSession = new StudentSession();
  public studentGroupSession: StudentGroupSession = new StudentGroupSession();
  public sessionSelectedDateInfo: SessionSelectedDateInfo = new SessionSelectedDateInfo();

  private _placementMaintenance$ = new BehaviorSubject<PlacementMaintenance>(null);
  private _placementData$ = new BehaviorSubject<PlacementData>(null);
  public readonly placementData$: Observable<PlacementData> = this._placementData$.asObservable();
  public readonly placementMaintenance$: Observable<PlacementMaintenance> = this._placementMaintenance$.asObservable();

  constructor() { }

  public setPlacementData(data: PlacementData): void {
    this._placementData$.next(data);
  }

  public setPlacementMaintenance(data: PlacementMaintenance): void {
    this._placementMaintenance$.next(data);
  }

  /**
   * Fills the booking header streams (placementData$, placementMaintenance$) for a placement. Arriving
   * from another screen for the same placement, both are already populated; on a deep link or refresh
   * (or a different placement, which ensureContext has just reset) they are null, so fetch whichever is
   * missing. Call after ensureContext.
   *
   * The caller passes its PlacementService: it is provided by AppModule rather than in root, so this
   * root service does not inject it.
   */
  public loadPlacementHeader(placementId: number, placementService: PlacementService, onError: (error: any) => void): void {
    if (this._placementData$.value == null) {
      placementService.getById(placementId).subscribe({
        next: (data: PlacementData) => this.setPlacementData(data),
        error: onError
      });
    }
    if (this._placementMaintenance$.value == null) {
      placementService.getPlacementMaintenanceInfo(placementId.toString(), true).subscribe({
        next: (maintenance: PlacementMaintenance) => this.setPlacementMaintenance(maintenance),
        error: onError
      });
    }
  }

  /**
   * Establishes the placement context from a route :id. Only rebuilds commonData when the
   * placement actually changes, so navigating between family screens (which share the same
   * placement) preserves any transient context already set (startTime/endTime/actionId).
   */
  public ensureContext(placementId: number): void {
    if (this.commonData == null || this.commonData.placementId !== placementId) {
      this.setCommonDataBeforeNavigate(placementId);
    }
  }

  public setCommonDataBeforeNavigate(placementId: number, startTime: Date = null, endTime: Date = null, actionId: number = null): void {
    // refresh other classes
    this._placementData$.next(null);
    this._placementMaintenance$.next(null);
    this.baseline = new Baseline;
    this.progressReport = new ProgressReportInfo();
    this.studentSession = new StudentSession();
    this.studentGroupSession = new StudentGroupSession();

    this.keyStages = new Array<KeyStage>();
    this.targetGrades = new Array<TargetGrade>();

    this.commonData = new CommonData();

    this.commonData.placementId = placementId;

    if (startTime != null) {
      this.commonData.startTime = startTime;
    }
    if (endTime != null) {
      this.commonData.endTime = endTime;
    }
    if (actionId != null) {
      this.commonData.actionId = actionId;
    }

  }

  public setIsStudentGroup(groupStudentId: number): void {
    this.commonData.groupStudentId = groupStudentId;
    this.commonData.isStudentGroup = true;
  }

  public setBaselineFrame(baselineFrame: BaselineFrame): void {
    
    this.baseline = new Baseline;
    this.keyStages = new Array<KeyStage>();
    this.targetGrades = new Array<TargetGrade>();

    this.baseline = baselineFrame.baseline;
    this.keyStages = baselineFrame.keyStages;
    this.targetGrades = baselineFrame.targetGrades;
    this.assessmentRequired = baselineFrame.assessmentRequired;
    this.baselineTargetForOutcome = baselineFrame.targetForOutcome;
    this.defaultIndividualLearningPlan = baselineFrame.defaultIndividualLearningPlan;

  }

}
